import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import classnames from 'clsx';

import { DropdownOverlay } from './dropdown-overlay';
import { DropdownToggler } from './dropdown-toggler';
// @ts-expect-error less import
import style from './dropdown.module.css';
import { noop } from '../../../default-props/noop';

/**
 * @typedef {import('./dropdown-types.d.ts').DropdownComponent} DropdownComponent
 */

/**
 * @type {DropdownComponent}
 */
export const Dropdown = React.forwardRef(
  (
    {
      ariaLabel,
      children = null,
      className = '',
      closeWhenChildrenClicked = true,
      containerAllowedMeToClose,
      dropdownMenuShouldHaveWidthOfButton = false,
      id = '',
      isOpen: isOpenProp,
      menuPosition = 'bottom-left',
      onBlur,
      onClose = noop,
      onFocus,
      onKeyDown = noop,
      onOpen = noop,
      openClassName = '',
      openTogglerClassName = '',
      ...props
    },
    incomingRef,
  ) => {
    const ref = useRef();
    const togglerRef = useRef();
    const overlayRef = useRef();

    useImperativeHandle(incomingRef, () => ({
      overlay: overlayRef,
      toggler: togglerRef,
    }));

    const [isOpen, setIsOpen] = useState(isOpenProp || false);
    const [isSettingFromState, setIsSettingFromState] = useState(false);

    const closeDropdown = useCallback(() => {
      setIsOpen(false);
      setIsSettingFromState(true);
    }, []);

    const openDropdown = useCallback(() => {
      setIsOpen(true);
      setIsSettingFromState(true);
    }, []);

    useEffect(() => {
      if (!isSettingFromState && isOpenProp !== undefined && isOpenProp !== isOpen) {
        setIsOpen(isOpenProp);
      }

      if (isSettingFromState) {
        setIsSettingFromState(false);
      }
    }, [isOpen, isOpenProp, isSettingFromState]);

    useEffect(() => {
      if (isOpen && onOpen) {
        onOpen();
      } else if (!isOpen && onClose) {
        onClose();
      }
    }, [isOpen, onClose, onOpen]);

    const [toggler, dropMenuItems] = React.Children.toArray(children);

    return (
      <div
        ref={ref}
        aria-label={ariaLabel}
        className={classnames(style.drop, className, {
          [openClassName]: isOpen,
        })}
        // When open, prevent document mousedown from calling onClose when the
        // user clicks the toggler — handleClick already closes it directly.
        data-ignore-click-outside={isOpen || undefined}
        data-testid={props['data-testid']}
        onKeyDown={onKeyDown}
      >
        <DropdownToggler
          ref={togglerRef}
          id={id}
          isOpen={isOpen}
          onBlur={onBlur}
          onClose={closeDropdown}
          onFocus={onFocus}
          onOpen={openDropdown}
          openTogglerClassName={openTogglerClassName}
        >
          {toggler}
        </DropdownToggler>
        {isOpen ? (
          <DropdownOverlay
            ref={overlayRef}
            closeWhenChildrenClicked={closeWhenChildrenClicked}
            containerAllowedMeToClose={containerAllowedMeToClose}
            dropdownMenuShouldHaveWidthOfButton={dropdownMenuShouldHaveWidthOfButton}
            isOpen={isOpen}
            menuPosition={menuPosition}
            onClose={closeDropdown}
            onKeyDown={onKeyDown}
            targetRef={ref}
          >
            {dropMenuItems}
          </DropdownOverlay>
        ) : null}
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';
